import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
} from "react-bootstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import PageLayout from "../../../layouts/PageLayout";

export default function ViewSchoolEnrollment() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);

  const AuthToken = localStorage.getItem("AuthUser");

  axios.defaults.headers.common[
    "Authorization"
  ] = `Bearer ${AuthToken}`;

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchEnrollment();
  }, [id]);

  const fetchEnrollment = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/school-enrollments/${id}`
      );

      setEnrollment(response.data.data);
    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error!",
        text:
          error.response?.data?.message ||
          "Failed to load school enrollment.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const storageUrl = (path) => {
    if (!path) return null;

    return `${API_URL.replace("/api", "")}/storage/${path}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined || amount === "") {
      return "-";
    }

    return Number(amount).toLocaleString();
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case "active":
        return "success";

      case "completed":
        return "primary";

      case "dropped":
        return "danger";

      case "transferred":
        return "warning";

      default:
        return "secondary";
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      </PageLayout>
    );
  }

  if (!enrollment) {
    return (
      <PageLayout>
        <Card>
          <Card.Body className="text-center py-5">
            <h4>School Enrollment Not Found</h4>

            <Button
              className="mt-3"
              onClick={() =>
                navigate("/school-enrollments")
              }
            >
              Back to List
            </Button>
          </Card.Body>
        </Card>
      </PageLayout>
    );
  }

  const student = enrollment.student;
  const school = enrollment.school;
  const schoolClass = enrollment.school_class;
  const session = enrollment.academic_session;
  const donor = enrollment.donor;

  return (
    <PageLayout>
      {/* Breadcrumb */}
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">
                School Enrollment Details
              </h3>

              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link
                    to="/"
                    className="mc-breadcrumb-link"
                  >
                    Home
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  <Link
                    to="/school-enrollments"
                    className="mc-breadcrumb-link"
                  >
                    School Enrollments
                  </Link>
                </li>

                <li className="mc-breadcrumb-item">
                  View Enrollment
                </li>
              </ul>
            </div>
          </div>
        </Col>
      </Row>

      {/* Header */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={8}>
              <h4 className="mb-1">
                {student?.name || "-"}
              </h4>

              <div className="text-muted">
                Enrollment No:{" "}
                <strong>
                  {enrollment.enrollment_no || "-"}
                </strong>
              </div>
            </Col>

            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Badge
                bg={getStatusVariant(
                  enrollment.status
                )}
                className="px-3 py-2"
              >
                {enrollment.status || "N/A"}
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Student Information */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            Student Information
          </h5>
        </Card.Header>

        <Card.Body>
          <Row>
            <InfoItem
              label="Student Name"
              value={student?.name}
            />

            <InfoItem
              label="Date of Birth"
              value={formatDate(student?.dob)}
            />

            <InfoItem
              label="Age"
              value={student?.age}
            />

            <InfoItem
              label="CNIC"
              value={student?.cnic}
            />

            <InfoItem
              label="Student ID"
              value={student?.id}
            />

            <InfoItem
              label="Family Person ID"
              value={student?.person_id}
            />
          </Row>
        </Card.Body>
      </Card>

      {/* Enrollment Information */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            Enrollment Information
          </h5>
        </Card.Header>

        <Card.Body>
          <Row>
            <InfoItem
              label="Enrollment Number"
              value={enrollment.enrollment_no}
            />

            <InfoItem
              label="Admission Date"
              value={formatDate(
                enrollment.admission_date
              )}
            />

            <InfoItem
              label="Roll Number"
              value={enrollment.roll_no}
            />

            <InfoItem
              label="Status"
              value={
                <Badge
                  bg={getStatusVariant(
                    enrollment.status
                  )}
                >
                  {enrollment.status || "N/A"}
                </Badge>
              }
            />

            <InfoItem
              label="Academic Session"
              value={session?.name}
            />

            <InfoItem
              label="Donor"
              value={donor?.name}
            />
          </Row>
        </Card.Body>
      </Card>

      {/* School Information */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            School Information
          </h5>
        </Card.Header>

        <Card.Body>
          <Row>
            <InfoItem
              label="School Name"
              value={school?.name}
            />

            <InfoItem
              label="School Code"
              value={school?.code}
            />

            <InfoItem
              label="Level"
              value={school?.level}
            />

            <InfoItem
              label="Authority"
              value={school?.authority}
            />

            <InfoItem
              label="Principal"
              value={school?.principal_name}
            />

            <InfoItem
              label="Contact Number"
              value={school?.contact_no}
            />

            <InfoItem
              label="Email"
              value={school?.email}
            />

            <InfoItem
              label="Address"
              value={school?.address}
            />
          </Row>
        </Card.Body>
      </Card>

      {/* Class / Session */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            Class & Academic Session
          </h5>
        </Card.Header>

        <Card.Body>
          <Row>
            <InfoItem
              label="Class"
              value={schoolClass?.name}
            />

            <InfoItem
              label="Class Description"
              value={schoolClass?.description}
            />

            <InfoItem
              label="Academic Session"
              value={session?.name}
            />

            <InfoItem
              label="Session Start"
              value={formatDate(
                session?.start_date
              )}
            />

            <InfoItem
              label="Session End"
              value={formatDate(
                session?.end_date
              )}
            />
          </Row>
        </Card.Body>
      </Card>

      {/* Financial Information */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            Financial Information
          </h5>
        </Card.Header>

        <Card.Body>
          <Row>
            <InfoItem
              label="Fee"
              value={
                enrollment.fee
                  ? `${formatAmount(
                    enrollment.fee
                  )} PKR`
                  : "-"
              }
            />

            <InfoItem
              label="Material Cost"
              value={
                enrollment.cost
                  ? `${formatAmount(
                    enrollment.cost
                  )} PKR`
                  : "-"
              }
            />

            <InfoItem
              label="Total Amount"
              value={
                enrollment.fee ||
                  enrollment.cost
                  ? `${formatAmount(
                    Number(
                      enrollment.fee || 0
                    ) +
                    Number(
                      enrollment.cost || 0
                    )
                  )} PKR`
                  : "-"
              }
            />
          </Row>
        </Card.Body>
      </Card>

      {/* Remarks */}
      {enrollment.remarks && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              Remarks
            </h5>
          </Card.Header>

          <Card.Body>
            <p className="mb-0">
              {enrollment.remarks}
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Images */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            Images
          </h5>
        </Card.Header>

        <Card.Body>
          <Row>
            <ImageCard
              title="Student Image"
              path={enrollment.student_image}
              storageUrl={storageUrl}
            />

            <ImageCard
              title="Going to School"
              path={enrollment.going_image}
              storageUrl={storageUrl}
            />
          </Row>
        </Card.Body>
      </Card>

      {/* Receipts */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            Receipts
          </h5>
        </Card.Header>

        <Card.Body>
          <Row>
            <ReceiptCard
              title="Fee Receipt"
              path={enrollment.fee_receipt}
              storageUrl={storageUrl}
            />

            <ReceiptCard
              title="Cost Receipt"
              path={enrollment.cost_receipt}
              storageUrl={storageUrl}
            />
          </Row>
        </Card.Body>
      </Card>

      {/* Video */}
      {enrollment.video && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              Enrollment Video
            </h5>
          </Card.Header>

          <Card.Body>
            <video
              controls
              style={{
                width: "100%",
                maxHeight: "500px",
                borderRadius: "10px",
              }}
            >
              <source
                src={storageUrl(
                  enrollment.video
                )}
              />
              Your browser does not support
              the video tag.
            </video>
          </Card.Body>
        </Card>
      )}

      {/* Footer Buttons */}
      <div className="d-flex justify-content-end gap-2 mb-4">
        <Button
          variant="secondary"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>


      </div>
    </PageLayout>
  );
}

/* ================================
   INFO ITEM
================================ */

function InfoItem({ label, value }) {
  return (
    <Col md={4} className="mb-3">
      <div>
        <small className="text-muted d-block mb-1">
          {label}
        </small>

        <strong>
          {value !== null &&
            value !== undefined &&
            value !== ""
            ? value
            : "-"}
        </strong>
      </div>
    </Col>
  );
}

/* ================================
   IMAGE CARD
================================ */

function ImageCard({
  title,
  path,
  storageUrl,
}) {
  if (!path) {
    return (
      <Col md={6} className="mb-3">
        <div className="border rounded p-3 text-center">
          <h6>{title}</h6>
          <p className="text-muted mb-0">
            No image available
          </p>
        </div>
      </Col>
    );
  }

  return (
    <Col md={6} className="mb-4">
      <h6 className="mb-2">{title}</h6>

      <div
        className="border rounded p-2"
        style={{
          background: "#f8f9fa",
        }}
      >
        <img
          src={storageUrl(path)}
          alt={title}
          style={{
            width: "100%",
            height: "300px",
            objectFit: "contain",
            borderRadius: "8px",
          }}
        />

        <div className="text-center mt-2">
          <a
            href={storageUrl(path)}
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm btn-primary"
          >
            View Full Image
          </a>
        </div>
      </div>
    </Col>
  );
}

/* ================================
   RECEIPT CARD
================================ */

function ReceiptCard({
  title,
  path,
  storageUrl,
}) {
  if (!path) {
    return (
      <Col md={6} className="mb-3">
        <div className="border rounded p-3 text-center">
          <h6>{title}</h6>
          <p className="text-muted mb-0">
            No receipt available
          </p>
        </div>
      </Col>
    );
  }

  return (
    <Col md={6} className="mb-3">
      <div className="border rounded p-3">
        <h6>{title}</h6>

        <div className="mt-3">
          <a
            href={storageUrl(path)}
            target="_blank"
            rel="noreferrer"
            className="btn btn-outline-primary"
          >
            <i className="material-icons me-1">
              receipt_long
            </i>
            View Receipt
          </a>
        </div>
      </div>
    </Col>
  );
}